import { __assign, __awaiter, __generator, __read } from "tslib";
import React, { useCallback, useEffect, useMemo, useRef, useState, } from "react";
import classNames from "classnames";
import { ScrollView, Swiper, SwiperItem, View } from "../_core/components";
import { pxTransform } from "../_util/px-transform";
import { Icon } from "../icon";
import { Badge } from "../badge";
import { useDefault } from "../_util/use-default";
import { useIsReady } from "../_util/use-is-ready";
import { getRect } from "../_util/get-rect";
import { TaroEnv } from "../_util/env";
import { useConfig } from "../_util/use-config";
import { forwardRefWithStatics } from "../_util/forward-ref-with-statics";
export var Tabs = forwardRefWithStatics(function Tabs(props, ref) {
    var _this = this;
    var classPrefix = useConfig().classPrefix;
    var children = props.children, _a = props.placement, placement = _a === void 0 ? "top" : _a, swipeable = props.swipeable, allowVerticalSwipe = props.allowVerticalSwipe, swipeContentHeight = props.swipeContentHeight, tabLayoutDirection = props.tabLayoutDirection, _b = props.animation, animation = _b === void 0 ? true : _b, className = props.className, style = props.style;
    var tabs = useMemo(function () { return (Array.isArray(children) ? children : [children]); }, [children]);
    var _c = __read(useDefault(props.activeId, props.defaultActiveId || (tabs[0] ? tabs[0].props.id : undefined), props.onActive), 2), activeId = _c[0], setActiveId = _c[1];
    var activeIndex = tabs.findIndex(function (x) { return x.props.id === activeId; });
    // 在 activeId 变更之后，要更新 TabsBar 的滚动位置
    var ready = useIsReady();
    var _d = __read(useState([0, 0]), 2), _e = __read(_d[0], 2), scrollLeft = _e[0], scrollTop = _e[1], setScrollingPosition = _d[1];
    var tabBarRef = useRef(null);
    var direction = ["top", "bottom"].includes(placement) ? "x" : "y";
    var updateScrollingPosition = useCallback(function () { return __awaiter(_this, void 0, void 0, function () {
        var _a, scrollRect, tabRects, tabRect, size, offset, scrolling;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!ready) {
                        return [2 /*return*/];
                    }
                    return [4 /*yield*/, getRect(tabBarRef, "." + classPrefix + "-tabs__scroll")];
                case 1:
                    _a = __read.apply(void 0, [_b.sent(), 1]), scrollRect = _a[0];
                    return [4 /*yield*/, getRect(tabBarRef, "." + classPrefix + "-tabs__item")];
                case 2:
                    tabRects = _b.sent();
                    if (activeIndex === -1 || !scrollRect || !tabRects[activeIndex]) {
                        return [2 /*return*/];
                    }
                    tabRect = tabRects[activeIndex];
                    size = direction === "x" ? "width" : "height";
                    offset = tabRects
                        .slice(0, activeIndex)
                        .reduce(function (prev, curr) { return prev + curr[size]; }, 0);
                    scrolling = offset - (scrollRect[size] - tabRect[size]) / 2;
                    setScrollingPosition(size === "width" ? [scrolling, 0] : [0, scrolling]);
                    return [2 /*return*/];
            }
        });
    }); }, [ready, classPrefix, activeIndex, direction]);
    useEffect(function () {
        if (ready) {
            updateScrollingPosition();
        }
    }, [ready, updateScrollingPosition]);
    var placementClassName = classPrefix + "-tabs__placement--" + placement;
    return (React.createElement(View, { className: classNames(classPrefix + "-tabs", placementClassName, className), style: style, ref: ref },
        React.createElement(View, { className: classPrefix + "-tabs__bar", ref: tabBarRef },
            React.createElement(ScrollView, { className: classPrefix + "-tabs__scroll", scrollWithAnimation: true, scrollX: direction === "x", scrollY: direction === "y", scrollLeft: scrollLeft, scrollTop: scrollTop, enhanced: true, showScrollbar: false },
                React.createElement(View, { className: classPrefix + "-tabs__list" }, tabs.map(function (_a) {
                    var tab = _a.props;
                    var activeClassName = tab.id === activeId ? "is-active" : "is-inactive";
                    var icon = typeof tab.icon === "string" ? { name: tab.icon } : tab.icon;
                    return (
                    // 选项卡按钮
                    React.createElement(View, { key: tab.id, className: classNames(classPrefix + "-tabs__item", activeClassName, classPrefix + "-tabs__direction--" + tabLayoutDirection, tab.className), style: tab.style, onClick: function () {
                            setActiveId(tab.id);
                        } },
                        icon && (React.createElement(Icon, __assign({ size: TaroEnv ? 36 : 18 }, icon, { className: classNames(classPrefix + "-tabs__icon", activeClassName, icon.className) }))),
                        React.createElement(View, { className: classPrefix + "-tabs__label" }, tab.label),
                        tab.badge && (React.createElement(Badge, __assign({}, tab.badge, { className: classNames(classPrefix + "-tabs__badge", tab.badge.className) }))),
                        tab.id === activeId && (React.createElement(View, { className: classNames(classPrefix + "-tabs__line", activeClassName) }))));
                }))),
            React.createElement(View, { className: classPrefix + "-tabs__lid" })),
        swipeable ? (React.createElement(Swiper, __assign({ key: direction, className: classPrefix + "-tabs__panel", current: activeIndex, vertical: direction === "y" && allowVerticalSwipe, onChange: function (event) {
                var current = event.detail.current;
                setActiveId(tabs[current].props.id);
            }, duration: 240, style: {
                height: typeof swipeContentHeight === "number"
                    ? pxTransform(swipeContentHeight, 750)
                    : swipeContentHeight,
            } }, { animationOnSetCurrent: animation }), tabs.map(function (_a) {
            var tab = _a.props;
            return (React.createElement(SwiperItem, { key: tab.id, className: classPrefix + "-tabs__content" }, tab.children));
        }))) : (React.createElement(View, { className: classPrefix + "-tabs__panel" }, tabs.map(function (_a) {
            var tab = _a.props;
            return (React.createElement(View, { key: tab.id, className: classNames(classPrefix + "-tabs__content", tab.id === activeId ? "is-active" : "is-inactive") }, tab.id === activeId ? tab.children : null));
        })))));
}, {
    Tab: TabPanel,
});
Tabs.displayName = "Tabs";
function TabPanel(props) {
    return React.createElement(View, null);
}
TabPanel.displayName = "TabPanel";
//# sourceMappingURL=Tabs.js.map